import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { getAdminApiBaseUrl, normalizeAdminRequestPath } from './adminApiPaths'
import { clearAccessToken, getAccessToken, setAccessToken } from './authSession'
import { getStoredCurrentOrg } from '../lib/currentOrgStorage'

const TRAILING_SLASHES_PATTERN = /\/+$/

type ViteImportMeta = ImportMeta & {
  env?: Record<string, string | undefined>
}

type AdminRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
  _skipAuthRefresh?: boolean
  _skipOrgRewrite?: boolean
}

type AuthRefreshConfig = AxiosRequestConfig & {
  _skipAuthRefresh?: boolean
}

// Use environment variable for API URL, fallback to relative path
const viteEnv = (import.meta as ViteImportMeta).env || {}

export function getAdminApiBaseUrlFromEnv(env: Record<string, string | undefined> = viteEnv): string {
  return getAdminApiBaseUrl(env.VITE_API_URL || '')
}

export function getFeedApiBaseUrlFromEnv(env: Record<string, string | undefined> = viteEnv): string {
  return (env.VITE_FEED_API_URL || '').replace(TRAILING_SLASHES_PATTERN, '')
}

const FEED_API_URL = getFeedApiBaseUrlFromEnv(viteEnv)

interface QueueItem {
  resolve: (value: string | null) => void
  reject: (error: unknown) => void
}

const api: AxiosInstance = axios.create({
  baseURL: getAdminApiBaseUrlFromEnv(viteEnv),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
})

const feedClient: AxiosInstance = axios.create()

let isRefreshing = false
let failedQueue: QueueItem[] = []

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

api.interceptors.request.use(
  (config: AdminRequestConfig) => {
    const token = getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    const currentOrg = getStoredCurrentOrg()

    // Handle :org placeholder
    if (config.url?.includes(':org')) {
      if (currentOrg?.slug) {
        config.url = config.url.replace(':org', currentOrg.slug)
      } else {
        console.warn('No organization selected for request:', config.url)
        return Promise.reject(new Error('No organization selected'))
      }
    }

    config.url = normalizeAdminRequestPath(
      config.url,
      currentOrg?.slug || '',
      Boolean(config._skipOrgRewrite)
    )

    return config
  },
  error => Promise.reject(error)
)

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AdminRequestConfig | undefined
    if (!originalRequest) {
      return Promise.reject(error)
    }

    const requestUrl = String(originalRequest?.url || '')
    const skipAuthRefresh =
      Boolean(originalRequest?._skipAuthRefresh) ||
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/refresh')

    if (error.response?.status === 401 && !originalRequest?._retry && !skipAuthRefresh) {
      if (isRefreshing) {
        return new Promise<string | null>((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return api(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const refreshConfig: AuthRefreshConfig = {
          _skipAuthRefresh: true,
        }
        const response = await api.post('/auth/refresh', undefined, {
          ...refreshConfig,
        })
        const { access_token } = response.data

        setAccessToken(access_token)
        processQueue(null, access_token)

        originalRequest.headers.Authorization = `Bearer ${access_token}`
        return api(originalRequest)
      } catch (refreshError) {
        clearAccessToken()
        processQueue(refreshError, null)
        localStorage.removeItem('current_org')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export { api, feedClient, FEED_API_URL }
export default api
